import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import './MeasurementForm.css';

function CalendarIcon() {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

/** yyyy-MM-dd in UTC for "today" (aligns with backend date validation). */
export function utcTodayIso(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Next calendar day after the given yyyy-MM-dd (interpreted as UTC). */
export function nextUtcDay(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const t = Date.UTC(y, m - 1, d) + 86400000;
  const dt = new Date(t);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toIso(y: number, month0: number, day: number): string {
  return `${y}-${pad2(month0 + 1)}-${pad2(day)}`;
}

function lastDayOfPreviousMonthIso(viewYear: number, viewMonth0: number): string {
  const t = Date.UTC(viewYear, viewMonth0, 0);
  const d = new Date(t);
  return toIso(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function getCalendarCells(
  viewYear: number,
  viewMonth0: number
): { iso: string; inMonth: boolean; key: string }[] {
  const firstDow = new Date(Date.UTC(viewYear, viewMonth0, 1)).getUTCDay();
  const leading = (firstDow + 6) % 7;
  const dim = new Date(Date.UTC(viewYear, viewMonth0 + 1, 0)).getUTCDate();
  const prevDim = new Date(Date.UTC(viewYear, viewMonth0, 0)).getUTCDate();

  const cells: { iso: string; inMonth: boolean; key: string }[] = [];

  for (let i = 0; i < leading; i++) {
    const day = prevDim - leading + i + 1;
    const pm = viewMonth0 === 0 ? 11 : viewMonth0 - 1;
    const py = viewMonth0 === 0 ? viewYear - 1 : viewYear;
    const iso = toIso(py, pm, day);
    cells.push({ iso, inMonth: false, key: `p-${iso}` });
  }
  for (let d = 1; d <= dim; d++) {
    const iso = toIso(viewYear, viewMonth0, d);
    cells.push({ iso, inMonth: true, key: `c-${iso}` });
  }
  const trailing = (7 - (cells.length % 7)) % 7;
  for (let i = 0; i < trailing; i++) {
    const day = i + 1;
    const nm = viewMonth0 === 11 ? 0 : viewMonth0 + 1;
    const ny = viewMonth0 === 11 ? viewYear + 1 : viewYear;
    const iso = toIso(ny, nm, day);
    cells.push({ iso, inMonth: false, key: `n-${iso}` });
  }
  return cells;
}

interface IsoDateFieldProps {
  id: string;
  value: string;
  min: string;
  onChange: (value: string) => void;
  disabled: boolean;
  required?: boolean;
}

/** yyyy-mm-dd display with an in-app calendar (native pickers are inconsistent, especially Firefox). */
function IsoDateField({
  id,
  value,
  min,
  onChange,
  disabled,
  required = false
}: IsoDateFieldProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => Number(min.slice(0, 4)));
  const [viewMonth0, setViewMonth0] = useState(() => Number(min.slice(5, 7)) - 1);

  const openPicker = () => {
    if (disabled) return;
    const base = value && value >= min ? value : min;
    const [y, m] = base.split('-').map(Number);
    setViewYear(y);
    setViewMonth0(m - 1);
    setOpen(true);
  };

  const closePicker = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rowRef.current?.contains(t) || popoverRef.current?.contains(t)) return;
      closePicker();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePicker();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const lastOfPrevIso = useMemo(
    () => lastDayOfPreviousMonthIso(viewYear, viewMonth0),
    [viewYear, viewMonth0]
  );
  const prevDisabled = lastOfPrevIso < min;

  const cells = useMemo(
    () => getCalendarCells(viewYear, viewMonth0),
    [viewYear, viewMonth0]
  );

  const monthLabel = useMemo(
    () =>
      new Date(Date.UTC(viewYear, viewMonth0, 1)).toLocaleDateString('en-GB', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC'
      }),
    [viewYear, viewMonth0]
  );

  const goPrevMonth = () => {
    if (prevDisabled) return;
    if (viewMonth0 === 0) {
      setViewYear((y) => y - 1);
      setViewMonth0(11);
    } else {
      setViewMonth0((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth0 === 11) {
      setViewYear((y) => y + 1);
      setViewMonth0(0);
    } else {
      setViewMonth0((m) => m + 1);
    }
  };

  const selectIso = (iso: string) => {
    if (iso < min) return;
    onChange(iso);
    closePicker();
  };

  const todayIso = utcTodayIso();

  return (
    <div ref={rowRef} className="measurement-iso-date-row">
      <input
        type="text"
        id={id}
        className="measurement-iso-date-display"
        readOnly
        role="combobox"
        aria-autocomplete="none"
        aria-readonly="true"
        value={value}
        placeholder="yyyy-mm-dd"
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        disabled={disabled}
        required={required}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        autoComplete="off"
      />
      <button
        type="button"
        className="measurement-iso-date-calendar-button"
        onClick={openPicker}
        disabled={disabled}
        aria-label="Open calendar"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
      >
        <CalendarIcon />
      </button>
      {open && (
        <div
          ref={popoverRef}
          id={popoverId}
          className="measurement-iso-date-popover"
          role="dialog"
          aria-label="Choose date"
        >
          <div className="measurement-iso-date-popover-header">
            <button
              type="button"
              className="measurement-iso-date-nav"
              aria-label="Previous month"
              disabled={prevDisabled}
              onClick={goPrevMonth}
            >
              ‹
            </button>
            <span className="measurement-iso-date-month-label">{monthLabel}</span>
            <button
              type="button"
              className="measurement-iso-date-nav"
              aria-label="Next month"
              onClick={goNextMonth}
            >
              ›
            </button>
          </div>
          <div className="measurement-iso-date-weekdays" aria-hidden>
            {WEEKDAYS.map((d) => (
              <span key={d} className="measurement-iso-date-weekday">
                {d}
              </span>
            ))}
          </div>
          <div className="measurement-iso-date-grid">
            {cells.map((cell) => {
              const dayNum = Number(cell.iso.slice(8, 10));
              const selectable = cell.iso >= min;
              const isValue = value === cell.iso;
              const isToday = cell.iso === todayIso;
              return (
                <button
                  key={cell.key}
                  type="button"
                  className={[
                    'measurement-iso-date-day',
                    !cell.inMonth && 'measurement-iso-date-day--outside',
                    isValue && 'measurement-iso-date-day--selected',
                    isToday && !isValue && 'measurement-iso-date-day--today',
                    !selectable && 'measurement-iso-date-day--disabled'
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={!selectable}
                  onClick={() => selectIso(cell.iso)}
                >
                  {dayNum}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

interface MeasurementDateRangeInputsProps {
  startDate: string;
  endDate: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  disabled?: boolean;
}

const MeasurementDateRangeInputs: React.FC<MeasurementDateRangeInputsProps> = ({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  disabled = false
}) => {
  const minStart = nextUtcDay(utcTodayIso());
  // End must be strictly after start; if start not chosen yet, require end after earliest allowed start.
  const minEnd = startDate ? nextUtcDay(startDate) : nextUtcDay(minStart);

  return (
    <>
      <div className="form-group">
        <label htmlFor="startDate">
          Start date <span className="required">*</span>
        </label>
        <IsoDateField
          id="startDate"
          value={startDate}
          min={minStart}
          onChange={onStartChange}
          disabled={disabled}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="endDate">
          End date <span className="required">*</span>
        </label>
        <IsoDateField
          id="endDate"
          value={endDate}
          min={minEnd}
          onChange={onEndChange}
          disabled={disabled}
          required
        />
      </div>
    </>
  );
};

export default MeasurementDateRangeInputs;
